import { clsx } from 'clsx'
import { Check } from 'lucide-react'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { MARKETING_PLAN_TIERS, PLAN_TIERS } from '@/lib/constants'
import { formatNumber } from '@/lib/format'

type Category = 'transactional' | 'marketing'

export function ChoosePlanPage(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams()
  const category: Category =
    searchParams.get('category') === 'marketing' ? 'marketing' : 'transactional'
  const [annual, setAnnual] = useState(false)
  const [loadingTier, setLoadingTier] = useState<string | null>(null)
  const navigate = useNavigate()

  const tiers = category === 'marketing' ? MARKETING_PLAN_TIERS : PLAN_TIERS

  function setCategory(c: Category): void {
    setSearchParams({ category: c })
  }

  async function handleSelectPlan(tierId: string): Promise<void> {
    setLoadingTier(tierId)
    try {
      const result = await apiClient.post<{ checkout_url: string }>(endpoints.billing.checkout, {
        tier: tierId,
        interval: annual ? 'year' : 'month',
        category,
      })
      window.location.href = result.checkout_url
    } catch {
      // If billing isn't set up yet, go to overview
      navigate('/')
    } finally {
      setLoadingTier(null)
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center bg-surface-1 px-4 py-12">
      <div className="max-w-5xl w-full">
        <div className="mb-10 text-center">
          <h1 className="text-3xl font-bold text-text-primary">Choose your plan</h1>
          <p className="mt-2 text-text-secondary">
            Start with a plan that fits your needs. Upgrade or downgrade anytime.
          </p>

          {/* Category toggle */}
          <div className="mt-6 inline-flex items-center gap-1 rounded-lg border border-surface-3 bg-surface-0 p-1">
            <button
              type="button"
              onClick={() => setCategory('transactional')}
              className={clsx(
                'rounded-md px-4 py-2 text-sm font-medium transition-colors',
                category === 'transactional'
                  ? 'bg-brand-500 text-white'
                  : 'text-text-secondary hover:text-text-primary',
              )}
            >
              Transactional
            </button>
            <button
              type="button"
              onClick={() => setCategory('marketing')}
              className={clsx(
                'rounded-md px-4 py-2 text-sm font-medium transition-colors',
                category === 'marketing'
                  ? 'bg-brand-500 text-white'
                  : 'text-text-secondary hover:text-text-primary',
              )}
            >
              Marketing
            </button>
          </div>

          {/* Annual/Monthly toggle */}
          <div className="mt-4 inline-flex items-center gap-3 rounded-lg border border-surface-3 bg-surface-0 p-1">
            <button
              type="button"
              onClick={() => setAnnual(false)}
              className={clsx(
                'rounded-md px-4 py-2 text-sm font-medium transition-colors',
                !annual ? 'bg-brand-500 text-white' : 'text-text-secondary hover:text-text-primary',
              )}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setAnnual(true)}
              className={clsx(
                'rounded-md px-4 py-2 text-sm font-medium transition-colors',
                annual ? 'bg-brand-500 text-white' : 'text-text-secondary hover:text-text-primary',
              )}
            >
              Annual
              <span className="ml-1.5 rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
                Save 20%
              </span>
            </button>
          </div>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {tiers.map((tier) => {
            const price = annual ? Math.round(tier.annualPrice / 12) : tier.monthlyPrice
            const isPopular = 'popular' in tier && tier.popular

            return (
              <div
                key={tier.id}
                className={clsx(
                  'relative flex flex-col rounded-2xl border bg-surface-0 p-6 shadow-sm',
                  isPopular ? 'border-brand-500 ring-2 ring-brand-500/20' : 'border-surface-3',
                )}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-brand-500 px-3 py-1 text-xs font-medium text-white">
                    Most Popular
                  </div>
                )}

                <h3 className="text-lg font-semibold text-text-primary">{tier.name}</h3>
                <div className="mt-3">
                  <span className="text-4xl font-bold text-text-primary">${price}</span>
                  <span className="text-text-secondary">/mo</span>
                </div>
                {annual && (
                  <p className="mt-1 text-xs text-text-tertiary">
                    ${tier.annualPrice}/year billed annually
                  </p>
                )}
                <p className="mt-2 text-sm text-text-secondary">
                  {formatNumber(tier.included)} emails/month
                </p>

                <ul className="mt-6 flex-1 space-y-3">
                  {tier.features.map((feature) => (
                    <li
                      key={feature}
                      className="flex items-start gap-2 text-sm text-text-secondary"
                    >
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  onClick={() => handleSelectPlan(tier.id)}
                  disabled={loadingTier !== null}
                  className={clsx(
                    'mt-6 w-full rounded-lg px-4 py-2.5 text-sm font-medium transition-colors disabled:opacity-50',
                    isPopular
                      ? 'bg-brand-500 text-white hover:bg-brand-600'
                      : 'border border-surface-3 bg-surface-0 text-text-primary hover:bg-surface-1',
                  )}
                >
                  {loadingTier === tier.id ? 'Redirecting...' : `Get ${tier.name}`}
                </button>
              </div>
            )
          })}
        </div>

        <p className="mt-8 text-center text-sm text-text-tertiary">
          All plans include a 10% free overage buffer. No credit card required for trial.
        </p>
      </div>
    </div>
  )
}
